import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseIcon from "@mui/icons-material/Close";
import {
  Backdrop,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import AppLoader from '../../../components/AppLoader/AppLoader';
import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import { companyName, getLogo } from "../../../services/Functions";
import { isEnabledDiscoCompany, sortEnabledDiscoCompanies } from "../../../utils/adminCompany";
import "./DistributionCompanySelector.scss";

const COMPANY_SHORT_NAMES = {
  11: "LESCO",
  12: "GEPCO",
  13: "FESCO",
  14: "IESCO",
  15: "MEPCO",
  26: "PESCO",
  27: "HAZECO",
  37: "HESCO",
  38: "SEPCO",
  48: "QESCO",
  59: "TESCO",
};

const getShortName = (companyCode, companyNameValue = "") => {
  const code = String(companyCode);
  if (COMPANY_SHORT_NAMES[code]) {
    return COMPANY_SHORT_NAMES[code];
  }
  const name = String(companyNameValue).trim();
  if (!name) return "";
  return name.split(/\s+/)[0].toUpperCase();
};

const getFullName = (company) =>
  company.companyName || companyName(String(company.companyCode));

export default function DistributionCompanySelector({
  open,
  companies,
  selectedCompanyCode,
  onSelectCompany,
  onConfirm,
  onClose,
  loading,
  companiesLoading,
  error,
}) {
  const cardRefs = useRef([]);
  const continueButtonRef = useRef(null);
  const enabledCompanies = sortEnabledDiscoCompanies(companies);
  const isBusy = loading || companiesLoading;
  const selectedShortName = selectedCompanyCode
    ? getShortName(selectedCompanyCode, getFullName({ companyCode: selectedCompanyCode }))
    : "";

  const handleConfirm = (companyCode) => {
    const code = String(companyCode || selectedCompanyCode || "");
    if (!code || isBusy || !isEnabledDiscoCompany(code)) {
      return;
    }
    onSelectCompany(code);
    onConfirm(code);
  };

  const handleCardClick = (code) => {
    if (isBusy) return;
    onSelectCompany(code);
    requestAnimationFrame(() => continueButtonRef.current?.focus());
  };

  const handleCardKeyDown = (event, code) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (isBusy) return;
      onSelectCompany(code);
      if (event.key === "Enter") {
        requestAnimationFrame(() => continueButtonRef.current?.focus());
      }
    }
  };

  useEffect(() => {
    if (!open || companiesLoading || !enabledCompanies.length) {
      return;
    }

    const focusIndex = enabledCompanies.findIndex(
      (company) => String(company.companyCode) === String(selectedCompanyCode),
    );
    const targetIndex = focusIndex >= 0 ? focusIndex : 0;
    cardRefs.current[targetIndex]?.focus();
  }, [open, companiesLoading, enabledCompanies, selectedCompanyCode]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
      }}
      fullWidth
      maxWidth="sm"
      className="distribution-company-dialog"
      disableEscapeKeyDown
      aria-labelledby="distribution-company-dialog-title"
      PaperProps={{
        className: "distribution-company-dialog__paper",
        sx: { position: "relative", overflow: "hidden" },
      }}
    >
      <div className="distribution-company-dialog__header">
        <div>
          <Typography
            id="distribution-company-dialog-title"
            component="h2"
            className="distribution-company-dialog__title"
          >
            Select Distribution Company
          </Typography>
          <Typography className="distribution-company-dialog__subtitle">
            Choose your company, then click Continue.
          </Typography>
        </div>

        <IconButton
          className="distribution-company-dialog__close"
          aria-label="Close and sign out"
          onClick={onClose}
          disabled={loading}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent className="distribution-company-dialog__content">
        {error ? (
          <Typography className="distribution-company-dialog__error" role="alert">
            {error}
          </Typography>
        ) : null}

        {companiesLoading ? (
          <Box
            className="distribution-company-dialog__loading"
            aria-label="Loading companies"
          >
            <AppLoader size={52}  />
          </Box>
        ) : (
          <Box className="distribution-company-grid" role="listbox" aria-label="Distribution companies">
            {enabledCompanies.map((company, index) => {
              const code = String(company.companyCode);
              const isSelected = selectedCompanyCode === code;
              const isConfirming = loading && isSelected;
              const logo = getLogo(code);
              const shortName = getShortName(code, company.companyName);
              const fullName = getFullName(company);

              return (
                <button
                  key={code}
                  ref={(node) => {
                    cardRefs.current[index] = node;
                  }}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  aria-busy={isConfirming}
                  className={`distribution-company-card${
                    isSelected ? " distribution-company-card--selected" : ""
                  }${isConfirming ? " distribution-company-card--loading" : ""}`}
                  onClick={() => handleCardClick(code)}
                  onKeyDown={(event) => handleCardKeyDown(event, code)}
                  disabled={loading}
                >
                  {isConfirming ? (
                    <span className="distribution-company-card__loading">
                      <span>Loading...</span>
                    </span>
                  ) : (
                    <>
                      <span className="distribution-company-card__logo-wrap">
                        <img src={logo} alt="" className="distribution-company-card__logo" />
                      </span>
                      <span className="distribution-company-card__name">{shortName}</span>
                      <span className="distribution-company-card__full-name">{fullName}</span>
                      {isSelected ? (
                        <CheckCircleRoundedIcon
                          className="distribution-company-card__check"
                          aria-hidden
                        />
                      ) : null}
                    </>
                  )}
                </button>
              );
            })}
          </Box>
        )}

        <Box className="distribution-company-dialog__actions">
          <Button
            ref={continueButtonRef}
            variant="contained"
            className={`distribution-company-dialog__continue${
              selectedCompanyCode && !isBusy
                ? " distribution-company-dialog__continue--ready"
                : ""
            }`}
            onClick={() => handleConfirm(selectedCompanyCode)}
            disabled={isBusy || !selectedCompanyCode}
            fullWidth
          >
            {loading
              ? "Please wait..."
              : selectedShortName
                ? `Continue with ${selectedShortName}`
                : "Continue"}
          </Button>
        </Box>
      </DialogContent>
      <Backdrop
        open={Boolean(loading)}
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.72)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AppLoader onLight label="Please wait..." />
      </Backdrop>
    </Dialog>
  );
}

DistributionCompanySelector.propTypes = {
  open: PropTypes.bool.isRequired,
  companies: PropTypes.arrayOf(
    PropTypes.shape({
      companyCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      companyName: PropTypes.string,
    }),
  ).isRequired,
  selectedCompanyCode: PropTypes.string,
  onSelectCompany: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  companiesLoading: PropTypes.bool,
  error: PropTypes.string,
};
